// Compatibility scoring engine
// Fast local scoring on A/B/C/D traits using construction team dynamics.
// This ranks candidates instantly with no API cost. Claude generates the
// deeper "why" narrative separately for top matches.

import { getArchetype } from "../data/archetypes";
import { roleFamily } from "./roles";

// Role categories for understanding pairing context
const FIELD_ROLES = ["Sr. Supt", "Supt", "Finish Supt", "Asst. Supt", "FE"];
const OFFICE_ROLES = ["Sr. PM", "PM", "APM", "PX"];

function isField(role) { return FIELD_ROLES.includes(role); }
function isOffice(role) { return OFFICE_ROLES.includes(role); }

// Score how well a candidate complements an anchor person.
// Returns { score: 0-100, factors: [{label, delta, note}] }
export function scorePairing(anchor, candidate, anchorRole, candidateRole) {
  let score = 50; // neutral baseline
  const factors = [];

  const a = { A: anchor.Survey_A, B: anchor.Survey_B, C: anchor.Survey_C, D: anchor.Survey_D };
  const c = { A: candidate.Survey_A, B: candidate.Survey_B, C: candidate.Survey_C, D: candidate.Survey_D };

  // ── 1. Autonomy balance (A) ───────────────────────────────
  // One leader should drive; two high-A people clash for control.
  if (a.A >= 6 && c.A >= 6) {
    score -= 12;
    factors.push({ label: "Autonomy clash", delta: -12, note: "Both high-autonomy — may compete for control." });
  } else if (a.A >= 6 || c.A >= 6) {
    score += 14;
    factors.push({ label: "Clear leader", delta: +14, note: "One drives, the other supports — healthy command structure." });
  } else {
    score -= 6;
    factors.push({ label: "No driver", delta: -6, note: "Neither is high-autonomy — team may lack direction." });
  }

  // ── 2. Pace compatibility (C) ─────────────────────────────
  // In construction, a fast PM + steady Super is the IDEAL combo, not a
  // problem — the PM drives urgency, the Super holds the line. So a healthy
  // pace gap is rewarded. Only an extreme gap with no shared ground is risky.
  const paceGap = Math.abs(a.C - c.C);
  if (paceGap >= 3 && paceGap <= 6) {
    score += 10;
    factors.push({ label: "Complementary pace", delta: +10, note: "One drives urgency, the other holds steady — the classic field/office rhythm." });
  } else if (paceGap >= 7) {
    score -= 6;
    factors.push({ label: "Extreme pace gap", delta: -6, note: "Very large pace difference — productive only with active management." });
  } else {
    score -= 4;
    factors.push({ label: "Same pace", delta: -4, note: "Similar work rhythm — no one balances the other's tempo." });
  }

  // ── 3. Quality/detail coverage (D) ────────────────────────
  // At least one person needs high-D for quality, safety, compliance.
  if (a.D >= 6 || c.D >= 6) {
    score += 10;
    factors.push({ label: "Quality anchored", delta: +10, note: "At least one detail-driven member — covers safety & compliance." });
  } else {
    score -= 12;
    factors.push({ label: "Detail gap", delta: -12, note: "Neither is high-conformity — quality control at risk." });
  }

  // ── 4. Communication / glue (B) ───────────────────────────
  // Some social ability helps a team cohere.
  if (a.B >= 6 || c.B >= 6) {
    score += 8;
    factors.push({ label: "Communication present", delta: +8, note: "At least one strong communicator to hold the team together." });
  } else {
    score -= 5;
    factors.push({ label: "Heads-down pair", delta: -5, note: "Both task-focused — morale & cross-talk need active attention." });
  }

  // ── 5. Field/office complementarity ───────────────────────
  // The PM/Super pairing is the heartbeat. Reward the classic combo of
  // an office driver (higher A) with a field stabilizer (higher C/D).
  if ((isOffice(candidateRole) && isField(anchorRole)) || (isOffice(anchorRole) && isField(candidateRole))) {
    const office = isOffice(anchorRole) ? a : c;
    const field = isField(anchorRole) ? a : c;
    if (office.A >= 6 && field.C >= 6) {
      score += 10;
      factors.push({ label: "Ideal PM/Super combo", delta: +10, note: "Office drives pace, field anchors quality — the textbook pairing." });
    }
  }

  // Clamp 0-100
  score = Math.max(0, Math.min(100, Math.round(score)));
  return { score, factors };
}

// Generate a one-line plain-English summary from the factors
export function summarizePairing(score, factors) {
  const positives = factors.filter(f => f.delta > 0).sort((a, b) => b.delta - a.delta);
  const negatives = factors.filter(f => f.delta < 0).sort((a, b) => a.delta - b.delta);

  let lead;
  if (score >= 80) lead = "Strong complement.";
  else if (score >= 65) lead = "Good fit.";
  else if (score >= 50) lead = "Workable.";
  else lead = "Some friction.";

  const parts = [lead];
  if (positives[0]) parts.push(positives[0].note);
  if (negatives[0] && score < 80) parts.push("Watch: " + negatives[0].note.toLowerCase());

  return parts.join(" ");
}

// Rank a pool of candidates against an anchor
export function rankCandidates(anchor, anchorRole, candidatePool, candidateRole) {
  return candidatePool
    .filter(p => p.FullName !== anchor.FullName)
    .map(candidate => {
      const { score, factors } = scorePairing(anchor, candidate, anchorRole, candidateRole);
      return {
        person: candidate,
        score,
        factors,
        summary: summarizePairing(score, factors),
        archetype: getArchetype(candidate.Archetype),
      };
    })
    .sort((a, b) => b.score - a.score);
}

export function scoreColor(score) {
  if (score >= 80) return { text: "var(--green)", bar: "var(--green-bar)" };
  if (score >= 65) return { text: "var(--green)", bar: "var(--green-bar)" };
  if (score >= 50) return { text: "var(--gold-text)", bar: "var(--gold)" };
  return { text: "var(--red)", bar: "var(--red-bar)" };
}

// ─── TEAM-LEVEL SCORING ──────────────────────────────────────
// Evaluates a whole roster for balance. Used by the project page (instant
// score ring) and Swap & Compare. Returns { score, coverage, factors }.
export function scoreTeam(people) {
  if (!people || people.length < 2) return null;
  let score = 55;
  const factors = [];
  const n = people.length;

  const highA = people.filter(p => p.Survey_A >= 6).length;
  const highB = people.filter(p => p.Survey_B >= 6).length;
  const highC = people.filter(p => p.Survey_C >= 6).length;
  const highD = people.filter(p => p.Survey_D >= 6).length;
  const lowC = people.filter(p => p.Survey_C <= 4).length;

  const coverage = {
    driver: highA >= 1,
    anchor: highC >= 1,
    quality: highD >= 1,
    glue: highB >= 1,
  };

  // Driver present (someone owns outcomes)
  if (coverage.driver) { score += 10; }
  else { score -= 12; factors.push({ delta: -12, note: "No clear driver — team may lack direction." }); }

  // Too many drivers (control clash)
  if (highA >= 3 && highA / n > 0.5) {
    score -= 10;
    factors.push({ delta: -10, note: "Several high-autonomy members — risk of competing for control." });
  }

  // Steady anchor present
  if (coverage.anchor) { score += 9; }
  else { score -= 10; factors.push({ delta: -10, note: "No steady anchor — fast-paced team may lack stability." }); }

  // Quality / detail present
  if (coverage.quality) { score += 10; }
  else { score -= 12; factors.push({ delta: -12, note: "No detail-driven member — quality & compliance at risk." }); }

  // Communicator / glue present
  if (coverage.glue) { score += 8; }
  else { score -= 6; factors.push({ delta: -6, note: "Light on communicators — morale & cross-talk need attention." }); }

  // Healthy pace mix (both urgency and steadiness)
  if (highC >= 1 && lowC >= 1) {
    score += 8;
    factors.push({ delta: +8, note: "Good pace mix — both urgency and steadiness present." });
  }

  score = Math.max(0, Math.min(100, Math.round(score)));
  return { score, coverage, factors };
}

export function teamVerdict(score) {
  if (score >= 80) return "Strong Compatibility";
  if (score >= 65) return "Solid Compatibility";
  if (score >= 50) return "Mixed Compatibility";
  return "Challenging Mix";
}

// ─── CANDIDATE PLACEMENT (phantom person → where do they fit?) ──
// Given a hypothetical person's traits, score how much they'd improve each
// team. Returns ranked teams with the score delta (team-with vs team-without).
export function placeCandidate(candidate, projects, teamMembers, allPeople) {
  return projects
    .map((proj) => {
      const members = teamMembers.filter((m) => m.ProjectJobNumber === proj.Title);
      const current = members.map((m) => allPeople.find((p) => p.FullName === m.PersonName)).filter(Boolean);
      if (current.length < 1) return null;

      const before = scoreTeam(current);
      const after = scoreTeam([...current, candidate]);
      if (!before || !after) return null;

      const delta = after.score - before.score;

      // What gap does this candidate fill?
      const fills = [];
      if (!before.coverage.driver && after.coverage.driver) fills.push("driver");
      if (!before.coverage.anchor && after.coverage.anchor) fills.push("anchor");
      if (!before.coverage.quality && after.coverage.quality) fills.push("quality");
      if (!before.coverage.glue && after.coverage.glue) fills.push("glue");

      return {
        project: proj,
        beforeScore: before.score,
        afterScore: after.score,
        delta,
        fills,
        teamSize: current.length,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.delta - a.delta);
}

// ─── DREAM TEAM BUILDER ──────────────────────────────────────
// Given locked (filled) members and a list of open roles, greedily fill each
// open role with the available person that most improves the team score.
export function buildDreamTeam(lockedPeople, openRoles, candidatePool) {
  let team = [...lockedPeople];
  const used = new Set(lockedPeople.map((p) => p.FullName));
  const fills = [];

  for (const role of openRoles) {
    // Only consider people in the SAME role family (a Super role pulls Sr./Asst./
    // Finish Supts, a PM role pulls Sr. PM/PM/APM, etc.) — never support (PX /
    // Project Accountant) or unrelated disciplines. Prefer an exact role match
    // first, then widen to the family; if no one in the family is free, leave the
    // seat open rather than filling it with the wrong discipline.
    const fam = roleFamily(role);
    const free = candidatePool.filter((p) => !used.has(p.FullName));
    let pool = free.filter((p) => p.CompanyRole === role);
    if (pool.length === 0) pool = free.filter((p) => roleFamily(p.CompanyRole) === fam);

    let best = null;
    let bestScore = -Infinity;
    const baseScore = scoreTeam(team)?.score ?? 0;

    for (const cand of pool) {
      const trial = scoreTeam([...team, cand]);
      const s = trial ? trial.score : 0;
      if (s > bestScore) {
        bestScore = s;
        best = cand;
      }
    }

    if (best) {
      team.push(best);
      used.add(best.FullName);
      fills.push({ role, person: best, delta: bestScore - baseScore, moved: best._deployedOn || null });
    } else {
      fills.push({ role, person: null, delta: 0, moved: null });
    }
  }

  const final = scoreTeam(team);
  return { fills, finalScore: final ? final.score : null, team };
}

// What single archetype/trait profile would most improve a given team?
// Returns the recommended complementary profile.
export function recommendComplement(teamPeople) {
  if (!teamPeople || teamPeople.length === 0) return null;
  // scoreTeam needs 2+ people; for a single anchor compute coverage directly
  // so the "optimal addition" hint still works when anchoring on one person.
  const ts = scoreTeam(teamPeople) || {
    coverage: {
      driver: teamPeople.some((p) => p.Survey_A >= 6),
      anchor: teamPeople.some((p) => p.Survey_C >= 6),
      quality: teamPeople.some((p) => p.Survey_D >= 6),
      glue: teamPeople.some((p) => p.Survey_B >= 6),
    },
  };

  const needs = [];
  if (!ts.coverage.driver) needs.push({ trait: "A", dir: "high", label: "a Driver", note: "high autonomy to own outcomes" });
  if (!ts.coverage.anchor) needs.push({ trait: "C", dir: "high", label: "a Steady Anchor", note: "high pace for methodical stability" });
  if (!ts.coverage.quality) needs.push({ trait: "D", dir: "high", label: "Quality Control", note: "high conformity for detail & compliance" });
  if (!ts.coverage.glue) needs.push({ trait: "B", dir: "high", label: "a Communicator", note: "high social ability to hold the team together" });

  // Map the top need to recommended archetypes
  const archByNeed = {
    A: ["Trailblazer", "Enterpriser", "Daredevil"],
    C: ["Craftsman", "Specialist", "Guardian"],
    D: ["Architect", "Technical Expert", "Scholar"],
    B: ["Persuader", "Rainmaker", "Influencer"],
  };

  if (needs.length === 0) {
    return { satisfied: true, note: "This team already covers all four behavioral roles. Add for capacity/skill, not balance." };
  }
  const top = needs[0];
  return {
    satisfied: false,
    primaryNeed: top,
    allNeeds: needs,
    archetypes: archByNeed[top.trait] || [],
  };
}
